/**
 * Utility functions for calendar operations (ICS generation, data URI handling)
 */

/**
 * Interface for calendar event parameters
 */
export interface CalendarEventParams {
  dateTime: Date;
  text: string;
  location: string;
  url: string;
}

/**
 * Generates an ICS (iCalendar) file string for calendar applications
 * @param params - Calendar event parameters
 * @returns Raw ICS file content as string
 */
export async function getRawIcsString(
  params: CalendarEventParams
): Promise<string> {
  const { dateTime, text, location, url } = params;

  // Format datetime for ICS (YYYYMMDDTHHMMSS) using local time (floating time)
  // Floating time (no Z suffix) means the time is interpreted in the calendar's local timezone
  const formatIcsDateTime = (date: Date): string => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const startDateTime = formatIcsDateTime(dateTime);

  // Set end time to 1 hour after start time
  const endDate = new Date(dateTime.getTime() + 60 * 60 * 1000);
  const endDateTime = formatIcsDateTime(endDate);

  // Generate unique ID for the event
  const uid = `${new Date().getTime()}@labcorp.com`;

  // Clean and format description text
  const description = text.replace(/\n/g, '\\n').replace(/,/g, '\\,');

  // ICS format requires lines to be no longer than 75 characters
  const foldLine = (line: string): string => {
    if (line.length <= 75) return line;
    const lines: string[] = [];
    let remaining = line;
    while (remaining.length > 75) {
      lines.push(remaining.substring(0, 75));
      remaining = ' ' + remaining.substring(75);
    }
    lines.push(remaining);
    return lines.join('\r\n');
  };

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Labcorp//Patient Appointments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${formatIcsDateTime(new Date())}`,
    `DTSTART:${startDateTime}`,
    `DTEND:${endDateTime}`,
    foldLine(`SUMMARY:Labcorp Appointment`),
    foldLine(`DESCRIPTION:${description}`),
    foldLine(`LOCATION:${location.replace(/\n/g, ' ').replace(/,/g, '\\,')}`),
    foldLine(`URL:${url}`),
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'BEGIN:VALARM',
    'TRIGGER:-PT1H',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder: Labcorp Appointment in 1 hour',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return icsContent;
}

/**
 * Converts ICS file content to a data URI
 * @param icsString - Raw ICS file content
 * @returns Data URI string
 */
export function getDataUri(icsString: string): string {
  const encoded = encodeURIComponent(icsString);
  return `data:text/calendar;charset=utf-8,${encoded}`;
}

/**
 * Triggers download of a file via data URI
 * @param document - Document object
 * @param dataUri - Data URI string
 * @param filename - Filename for the download
 */
export function triggerDataUriDownload(
  document: Document,
  dataUri: string,
  filename: string
): void {
  const link = document.createElement('a');
  link.href = dataUri;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
